import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../translations';

const HomePage = () => {
  const { language } = useLanguage();

  const modalityKeys = [
    'somaticTherapy', 'breathwork', 'energyHealing', 'soundTherapy',
    'plantMedicine', 'bodywork', 'quantumBioresonance', 'ancestralHealing',
    'movement', 'coaching', 'waterTherapy', 'fireCeremony',
  ];

  return (
    <main className="min-h-screen bg-[#FCF7EC] pt-20">
      {/* Hero Section - Logo Left, Text Right */}
      <section 
        data-testid="hero-section"
        className="min-h-[calc(100vh-80px)] flex items-center px-6 md:px-12 lg:px-20"
      >
        <div className="max-w-7xl mx-auto w-full">
          <div className="grid md:grid-cols-2 gap-8 md:gap-16">
            {/* Logo - Left, positioned higher */}
            <motion.div
              className="md:self-start md:ml-auto"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_mindful-container/artifacts/uwax7gvg_Screenshot%202026-04-05%20at%2021.24.59.png"
                alt="CONTAINER - For Personal Healing"
                className="w-full max-w-[400px] mx-auto md:mx-0"
              />
            </motion.div>

            {/* Text - Right, positioned lower */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-left md:self-end md:pb-8"
            >
              <p className="text-sm md:text-base font-sans leading-relaxed mb-1 md:whitespace-nowrap" style={{ color: '#12552B' }}>
                {t('home.heroLine1', language)}
              </p>
              <p className="text-sm md:text-base font-sans leading-relaxed mb-6 md:whitespace-nowrap" style={{ color: '#12552B' }}>
                {t('home.heroLine2', language)}
              </p>

              {/* CTA Button */}
              <Link
                to="/contact"
                data-testid="hero-cta-button"
                className="inline-block btn-primary px-8 py-3 font-sans text-sm tracking-wide rounded-full"
              >
                {t('nav.bookCall', language)}
              </Link>
            </motion.div>
          </div>

          {/* New text centered below logo and CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-center mt-16"
          >
            <p className="text-sm md:text-base font-sans leading-relaxed mb-1 md:whitespace-nowrap" style={{ color: '#12552B' }}>
              {t('home.heroLine3', language)}
            </p>
            <p className="text-sm md:text-base font-sans leading-relaxed mb-1 md:whitespace-nowrap" style={{ color: '#12552B' }}>
              {language === 'fr' ? "Le corps ne travaille pas contre nous, il communique. Guérir ne consiste pas à réparer ce qui est cassé." 
                : language === 'pt' ? 'O corpo não trabalha contra nós, está a comunicar. Curar não é consertar o que está partido.' 
                : 'The body is not working against us, it is communicating. Healing is not about fixing what is broken.'}
            </p>
            <p className="text-sm md:text-base font-sans leading-relaxed" style={{ color: '#12552B' }}>
              {language === 'fr' ? "C'est créer suffisamment de sécurité pour que le corps libère ce qu'il n'a plus besoin de retenir." 
                : language === 'pt' ? 'É criar segurança suficiente para que o corpo liberte o que já não precisa de reter.' 
                : 'It is about creating enough safety for the body to release what it no longer needs to hold.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Philosophy Section - Teal Background */}
      <section 
        data-testid="philosophy-section"
        className="min-h-screen flex items-center px-6 md:px-12 lg:px-20 py-20"
        style={{ backgroundColor: '#006868' }}
      >
        <div className="max-w-7xl mx-auto w-full">
          <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
            {/* Text - Left */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="text-white"
            >
              <p className="text-xs tracking-[0.3em] uppercase font-sans mb-4 opacity-80">
                {t('home.philosophyLabel', language)}
              </p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif mb-4">
                {t('home.philosophyTitle', language)}
              </h2>
              <p className="font-sans leading-relaxed mb-6 opacity-90">
                {t('home.philosophyP1', language)}
              </p>
              <p className="font-sans leading-relaxed mb-6 opacity-90">
                {t('home.philosophyP2', language)}
              </p>
              <p className="font-sans leading-relaxed mb-6 opacity-90 font-serif italic">
                {t('home.philosophyP3', language)}
              </p>
              <p className="font-sans leading-relaxed mb-6 opacity-90">
                {t('home.philosophyP4', language)}
              </p>
              <p className="font-sans leading-relaxed opacity-90">
                {t('home.philosophyP5', language)}
              </p>
            </motion.div>

            {/* Circles - Right - Overlapping with text outside */}
            <motion.div 
              className="relative w-full max-w-md mx-auto aspect-square"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              {/* The Home - Top Left Circle (Spruce Green) */}
              <div className="absolute top-[10%] left-0 w-[60%] h-[60%]">
                <div 
                  className="w-full h-full rounded-full"
                  style={{ backgroundColor: 'rgba(18, 85, 43, 0.3)' }}
                />
                <div className="absolute top-[25%] left-[10%] text-left">
                  <h3 className="font-serif text-base text-white mb-1">{t('home.circleHome', language)}</h3>
                  <p className="text-xs text-white/80 leading-relaxed max-w-[140px]">
                    {t('home.circleHomeDesc', language)}
                  </p>
                </div>
              </div>

              {/* The Protocol - Top Right Circle (Deep Teal) */}
              <div className="absolute top-[10%] right-0 w-[60%] h-[60%]">
                <div 
                  className="w-full h-full rounded-full"
                  style={{ backgroundColor: 'rgba(30, 71, 72, 0.3)' }}
                />
                <div className="absolute top-[25%] right-[10%] text-right">
                  <h3 className="font-serif text-base text-white mb-1">{t('home.circleProtocol', language)}</h3>
                  <p className="text-xs text-white/80 leading-relaxed max-w-[140px] ml-auto">
                    {t('home.circleProtocolDesc', language)}
                  </p>
                </div>
              </div>

              {/* The People - Bottom Center Circle (Linen) */}
              <div className="absolute bottom-[-5%] left-1/2 -translate-x-1/2 w-[60%] h-[60%]">
                <div 
                  className="w-full h-full rounded-full"
                  style={{ backgroundColor: 'rgba(241, 230, 211, 0.3)' }}
                />
                <div className="absolute top-[50%] left-1/2 -translate-x-1/2 text-center">
                  <h3 className="font-serif text-base text-white mb-1">{t('home.circlePeople', language)}</h3>
                  <p className="text-xs text-white/80 leading-relaxed max-w-[160px]">
                    {t('home.circlePeopleDesc', language)}
                  </p>
                </div>
              </div>

              {/* CONTAINER text in center - Golden Yellow */}
              <div className="absolute top-[55%] left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                <span 
                  className="font-serif text-lg tracking-wider"
                  style={{ color: '#FFBF00' }}
                >
                  CONTAINER
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Personal Retreat Section - Linen Background */}
      <section 
        data-testid="retreat-section"
        className="py-24 md:py-32 px-6 md:px-12 lg:px-20"
        style={{ backgroundColor: '#F1E6D3' }}
      >
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-xs tracking-[0.3em] uppercase font-sans text-teal mb-4">
              {t('home.retreatLabel', language)}
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-spruce mb-6">
              {t('home.retreatTitle', language)}
            </h2>
            <p className="text-lg text-deep-teal font-sans max-w-3xl mx-auto leading-relaxed">
              {t('home.retreatDesc', language)}
            </p>
          </motion.div>

          {/* Three Features */}
          <div className="grid md:grid-cols-3 gap-8 md:gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center"
            >
              <h3 className="text-xl font-serif text-spruce mb-4">{t('home.multipleHealers', language)}</h3>
              <p className="text-deep-teal font-sans leading-relaxed">
                {t('home.multipleHealersDesc', language)}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <h3 className="text-xl font-serif text-spruce mb-4">{t('home.personalizedProtocol', language)}</h3>
              <p className="text-deep-teal font-sans leading-relaxed">
                {t('home.personalizedProtocolDesc', language)}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-center"
            >
              <h3 className="text-xl font-serif text-spruce mb-4">{t('home.integratedModalities', language)}</h3>
              <p className="text-deep-teal font-sans leading-relaxed">
                {t('home.integratedModalitiesDesc', language)}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Modalities Marquee Banner - Teal */}
      <section 
        className="py-8"
        style={{ backgroundColor: '#006868' }}
      >
        <Marquee
          speed={80}
          gradient={false}
          pauseOnHover={true}
        >
          {modalityKeys.map((key, index) => (
            <div key={index} className="flex items-center mx-8">
              <span className="text-xl md:text-2xl font-serif text-white whitespace-nowrap">
                {t(`modalities.${key}`, language)}
              </span>
              <span className="text-golden mx-8">&#10022;</span>
            </div>
          ))}
        </Marquee>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 px-6 bg-[#FCF7EC]">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-serif text-spruce mb-6">
              {t('home.readyToBegin', language)}
            </h2>
            <p className="text-deep-teal font-sans leading-relaxed mb-8">
              {t('home.readyToBeginDesc', language)}
            </p>
            <Link
              to="/contact"
              className="inline-block btn-primary px-10 py-4 font-sans text-base tracking-wide rounded-full"
            >
              {t('nav.bookCall', language)}
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
